import { ChangeEvent, Dispatch, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { SelectChangeEvent } from '@mui/material/Select';
import { AutoAwesome } from '@mui/icons-material';
import dayjs from 'dayjs';
import { parseWallClockTime } from '../../../shared/Helpers/timezones';
import { toast } from 'react-toastify';

import { ChipSelect, CustomAlert, CustomButton, CustomCheckBox, CustomInput, CustomSelect, Text } from '../../../shared/uiComponents';
import { ORANGE, RED } from '../../../shared/Helpers/colors';
import { validateName } from '../../../shared/Helpers/functions';
import { BT_LOCATION_MISMATCH_ERROR } from '../../../shared/Helpers/constants';
import { AdminTypes } from '../../../components/Menu';
import { InfoRow } from '../../../components/StyledComponents';
import { useBehaviorTreatmentOptions } from '../hook';
import {
	ACTIONS,
	ActionProperties,
	ERRORS,
	behaviorTreatmentErrorReducer,
	initialBehaviorTreatmentErrorState,
} from './behaviorTreatmentReducer';
import { SubmitValidationIssue, validateBehaviorTreatmentForSubmit } from './behaviorTreatmentValidation';
import { areUntouchedHardDataSelections, isUntouchedHardDataText } from './aiFieldPolicy';
import { Behaviors, SignatureInput, SoapNoteButtons, Targets, TimeInfo } from '../components';
import { useSignatureDate, useSoapNoteFieldDisabled } from '../helpers';
import AIAssistSoapNote from '../AIAssist';
import { AiParseSoapNoteResponse } from '../../../redux/API/ClientAPIHelpers/ai';
import { isDoeFunder } from '../../../redux/API/constants';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getSupervisorSessionsInfo } from '../../../redux/State/clientSlice/sessionSlice';
import {
	BehaviorTreatmentProperties,
	LocationTypes,
	UpdateBehaviorTreatmentProperties,
} from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';
import { SessionTargetsProperties, SoapNotesStatuses } from '../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { DataCollection } from '../../../redux/API/ClientAPIHelpers/dataCollection';
import { Client } from '../../../redux/API/ClientAPIHelpers/client';
import { TargetSessionStatuses } from '../../../redux/API/ClientAPIHelpers/sessionsProperties';

const frequencyAndDurationDescription = `Describe frequency, duration and topography of maladaptive behavior.
Describe techniques used as interventions for the behaviors.
State if no maladaptive behaviors occurred.`;

type BehaviorHardDataRecord = {
	count?: number;
	duration?: number;
	durationInstances?: Array<{ duration: string }>;
	name: string;
};

const buildBehaviorSummaryText = (behaviors: BehaviorHardDataRecord[]): string => {
	if (!behaviors.length) return '';

	return behaviors
		.map((b) => {
			if (b.count != null) {
				return `${b.name} occurred ${b.count} time${b.count === 1 ? '' : 's'}.`;
			}

			if (b.duration != null) {
				const mins = Math.round(b.duration / 60);

				return `${b.name} total duration: ${mins > 0 ? `${mins} min` : '<1 min'}.`;
			}

			return `${b.name} recorded.`;
		})
		.join(' ');
};

const buildWorkedGoalsSummaryText = (targets: SessionTargetsProperties[]): string => {
	if (!targets.length) return '';

	return targets
		.map((t) => {
			const parts = [
				`Worked on ${t.target.name} (${t.independent}/${t.trials} trials correct, ${t.correctPercentage}%).`,
			];

			if (t.targetStatus === TargetSessionStatuses.new) {
				parts.push('This target was newly introduced this session.');
			}

			if (t.target.sd) {
				parts.push(`SD: "${t.target.sd}".`);
			}

			if (t.target.targetInstructions) {
				parts.push(`Instructions: ${t.target.targetInstructions}`);
			}

			return parts.join(' ');
		})
		.join(' ');
};

// Union deterministic selected IDs with newly matched IDs for untouched hard-data-backed fields.
const mergeIds = (existingIds: string[], newIds: string[]): string[] => {
	const set = new Set(existingIds);
	newIds.forEach((id) => set.add(id));

	return Array.from(set);
};

// Hard-data sources (per-client, free-text skill-area/reinforcement names) rarely match the fixed
// option strings character-for-character — e.g. a skill area named "Language and Communication:"
// vs the option "Language/communication". Reduce both sides to a sorted set of alphanumeric tokens
// (dropping punctuation like "/" and ":", the connector word "and", and "&") so semantically equal
// values compare equal without resorting to loose substring/fuzzy matching.
const normalizeToTokenKey = (value: string): string =>
	value
		.toLocaleLowerCase()
		.replace(/&/g, ' ')
		.replace(/[^a-z0-9]+/g, ' ')
		.split(' ')
		.filter((token) => token && token !== 'and')
		.sort()
		.join(' ');

const findOptionId = (options: Array<{ id: string | number; text: string }>, value: string): string | undefined => {
	const normalizedValue = value.trim().toLocaleLowerCase();
	const exactMatch = options.find((option) => option.text.trim().toLocaleLowerCase() === normalizedValue);
	if (exactMatch) return String(exactMatch.id);

	const tokenKey = normalizeToTokenKey(value);
	if (!tokenKey) return undefined;
	const tokenMatch = options.find((option) => normalizeToTokenKey(option.text) === tokenKey);

	return tokenMatch ? String(tokenMatch.id) : undefined;
};

const BehaviorTreatmentData = ({
	data,
	setData,
	save,
	submit,
	hydratedReportId,
}: {
	data: UpdateBehaviorTreatmentProperties;
	hydratedReportId: string | null;
	save: () => void;
	setData: Dispatch<ActionProperties>;
	submit: () => void;
}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const reportId = params.get('reportId') || '';
	const { user, sessionType } = useSelector((state) => state.session.sessionInfo);
	const isDoeFunded = isDoeFunder(sessionType?.funderId);

	const soapNote = useSelector((state) => state.soapNote.soapNote) as BehaviorTreatmentProperties | null;
	const signatureDate = useSignatureDate();
	const locationList = useSelector((state) => state.soapNote.locations);
	const supervisor = useSelector((state) => state.soapNote.soapNoteData.supervisor);
	const supervisorSessionInfo = useSelector((state) => state.session.supervisorSessionInfo);
	const errorList = useSelector((state) => state.soapNote.error?.errors);

	const { getFieldDisabled, canEdit, isPostSubmitEditMode } = useSoapNoteFieldDisabled();

	const [locations, setLocations] = useState<LocationTypes[]>([]);
	const [errors, setErrors] = useReducer(behaviorTreatmentErrorReducer, initialBehaviorTreatmentErrorState);
	const fieldErrors = useMemo(() => {
		const merged = { ...errors };
		const serverErrorKeys = new Set(Object.keys(errorList ?? {}));
		(Object.keys(ERRORS) as Array<keyof typeof ERRORS>).forEach((key) => {
			if (serverErrorKeys.has(key)) merged[key] = true;
		});

		return merged;
	}, [errors, errorList]);
	const [pendingAiSave, setPendingAiSave] = useState(false);
	const [autoFillCompleteReportId, setAutoFillCompleteReportId] = useState<string | null>(null);
	const [ambiguousFields, setAmbiguousFields] = useState<Set<string>>(new Set());
	const [aiModalOpen, setAiModalOpen] = useState(false);

	const behaviorTreatmentHardData = useSelector((state) => state.soapNote.behaviorTreatmentHardData);

	const {
		barriersList,
		technuquiesList,
		behaviorTreatmentFutureList,
		clientMoodList,
		clientResponseToTreatmentList,
		levelOfMaladaptiveBehaviorList,
		maladaptiveBehaviorTechniquesList,
		skillDomainAddressedList,
		reinforcementList,
		participantsList,
		optionsReady,
	} = useBehaviorTreatmentOptions();

	const hardDataSkillDomainIds = useMemo(() => (behaviorTreatmentHardData?.skillDomainNames || [])
		.map((name) => findOptionId(skillDomainAddressedList, name))
		.filter(Boolean) as string[], [behaviorTreatmentHardData, skillDomainAddressedList]);
	const hardDataReinforcementIds = useMemo(() => (behaviorTreatmentHardData?.reinforcementTitles || [])
		.map((title) => findOptionId(reinforcementList, title))
		.filter(Boolean) as string[], [behaviorTreatmentHardData, reinforcementList]);
	const replaceableHardDataFields = useMemo(() => {
		if (!soapNote || !behaviorTreatmentHardData) return [];

		const fields: string[] = [];
		if (isUntouchedHardDataText(soapNote.workedGoals, data.workedGoals, behaviorTreatmentHardData.workedGoals)) {
			fields.push('workedGoals');
		}
		if (isUntouchedHardDataText(
			soapNote.frequencyAndDurationOfMaladaptiveBehaviors,
			data.frequencyAndDurationOfMaladaptiveBehaviors,
			behaviorTreatmentHardData.frequencyAndDurationOfMaladaptiveBehaviors,
		)) {
			fields.push('frequencyAndDurationOfMaladaptiveBehaviors');
		}
		if (areUntouchedHardDataSelections(
			soapNote.skillDomainAddressedResponses?.map((option) => option.id) ?? [],
			data.skillDomainAddressedResponses,
			hardDataSkillDomainIds,
		)) {
			fields.push('skillDomainAddressed');
		}
		if (areUntouchedHardDataSelections(
			soapNote.reinforcementResponses?.map((option) => option.id) ?? [],
			data.reinforcementResponses,
			hardDataReinforcementIds,
		)) {
			fields.push('reinforcers');
		}

		return fields;
	}, [
		behaviorTreatmentHardData,
		data.frequencyAndDurationOfMaladaptiveBehaviors,
		data.reinforcementResponses,
		data.skillDomainAddressedResponses,
		data.workedGoals,
		hardDataReinforcementIds,
		hardDataSkillDomainIds,
		soapNote,
	]);

	const hardDataDefaultsApplied = useMemo(() => {
		if (!optionsReady || !soapNote) return false;
		if (!behaviorTreatmentHardData) return true;

		return (!behaviorTreatmentHardData.workedGoals || Boolean(data.workedGoals)) &&
			(!behaviorTreatmentHardData.frequencyAndDurationOfMaladaptiveBehaviors ||
				Boolean(data.frequencyAndDurationOfMaladaptiveBehaviors)) &&
			hardDataSkillDomainIds.every((id) => data.skillDomainAddressedResponses.includes(id)) &&
			hardDataReinforcementIds.every((id) => data.reinforcementResponses.includes(id));
	}, [
		behaviorTreatmentHardData,
		data.frequencyAndDurationOfMaladaptiveBehaviors,
		data.reinforcementResponses,
		data.skillDomainAddressedResponses,
		data.workedGoals,
		hardDataReinforcementIds,
		hardDataSkillDomainIds,
		optionsReady,
		soapNote,
	]);

	const autoFillAttempted = useRef(false);
	// Cache the in-flight/settled PROMISE (rather than checking-then-setting a plain value ref
	// after an await) so concurrent callers within the same render cycle share one request instead
	// of racing — assigning the ref happens synchronously, before any awaiting occurs.
	const behaviorHardDataPromiseRef = useRef<Promise<string> | null>(null);
	const workedGoalsHardDataPromiseRef = useRef<Promise<string> | null>(null);

	const getBehaviorHardDataText = useCallback((): Promise<string> =>
		Promise.resolve(behaviorTreatmentHardData?.frequencyAndDurationOfMaladaptiveBehaviors || ''),
	[behaviorTreatmentHardData]);

	// Raw session-target hard data (goal names/trial counts), passed to the AI so it can weave it
	// together with the BT's own narrative into one cohesive "workedGoals" block server-side,
	// instead of the client stitching two disjointed sentences together.
	const getWorkedGoalsHardDataText = useCallback(() => Promise.resolve(behaviorTreatmentHardData?.workedGoals || ''), [behaviorTreatmentHardData]);

	useEffect(() => {
		if (!soapNote || hydratedReportId !== reportId || !optionsReady || !behaviorTreatmentHardData) return;

		const skillDomainIds = hardDataSkillDomainIds;

		if (!data.workedGoals && behaviorTreatmentHardData.workedGoals) {
			setData({ payload: behaviorTreatmentHardData.workedGoals, type: ACTIONS.setWorkedGoals });
		}
		if (!data.frequencyAndDurationOfMaladaptiveBehaviors && behaviorTreatmentHardData.frequencyAndDurationOfMaladaptiveBehaviors) {
			setData({
				payload: behaviorTreatmentHardData.frequencyAndDurationOfMaladaptiveBehaviors,
				type: ACTIONS.setFrequencyAndDurationOfMaladaptiveBehaviors,
			});
		}
		const reinforcementIds = hardDataReinforcementIds;

		if (!data.skillDomainAddressedResponses.length && skillDomainIds.length) {
			setData({ payload: skillDomainIds, type: ACTIONS.setSkillDomainAddressed });
		}
		if (!data.reinforcementResponses.length && reinforcementIds.length) {
			setData({ payload: reinforcementIds, type: ACTIONS.setReinforcementResponses });
		}
		if (
			(!data.workedGoals && behaviorTreatmentHardData.workedGoals) ||
			(!data.frequencyAndDurationOfMaladaptiveBehaviors && behaviorTreatmentHardData.frequencyAndDurationOfMaladaptiveBehaviors) ||
			(!data.skillDomainAddressedResponses.length && skillDomainIds.length) ||
			(!data.reinforcementResponses.length && reinforcementIds.length)
		) {
			setPendingAiSave(true);
		}
	}, [
		behaviorTreatmentHardData,
		data.frequencyAndDurationOfMaladaptiveBehaviors,
		data.skillDomainAddressedResponses,
		data.reinforcementResponses,
		data.workedGoals,
		hydratedReportId,
		optionsReady,
		reportId,
		setData,
		hardDataReinforcementIds,
		hardDataSkillDomainIds,
		skillDomainAddressedList,
		soapNote,
	]);

	useEffect(() => {
		const sessionId = params.get('sessionId');
		if (!soapNote || hydratedReportId !== reportId || !optionsReady) return;
		if (!canEdit || !sessionId || !skillDomainAddressedList.length) {
			setAutoFillCompleteReportId(reportId);

			return;
		}
		if (autoFillAttempted.current) return;

		const needsGoals = false;
		const needsBehaviors = false;
		const needsSkillDomains = false;
		const needsReinforcers = false;

		if (!needsGoals && !needsBehaviors && !needsSkillDomains && !needsReinforcers) {
			setAutoFillCompleteReportId(reportId);

			return;
		}

		autoFillAttempted.current = true;

		let active = true;
		const fetchAndFill = async () => {
			try {
				const [targetsResponse, behaviorSummaryText, reinforcementsResponse] = await Promise.all([
					(needsGoals || needsSkillDomains)
						? DataCollection.sessions.targets.getSessionTargets(sessionId)
						: Promise.resolve([]),
					needsBehaviors ? getBehaviorHardDataText() : Promise.resolve(''),
					Promise.resolve([]),
				]);
				if (!active) return;

				const targets = targetsResponse as SessionTargetsProperties[];

				if (needsGoals && targets.length) {
					const workedGoalsText = buildWorkedGoalsSummaryText(targets);
					workedGoalsHardDataPromiseRef.current = Promise.resolve(workedGoalsText);
					setData({ payload: workedGoalsText, type: ACTIONS.setWorkedGoals });
				}

				if (needsBehaviors && behaviorSummaryText) {
					setData({
						payload: behaviorSummaryText,
						type: ACTIONS.setFrequencyAndDurationOfMaladaptiveBehaviors,
					});
				}

				if (needsSkillDomains && targets.length) {
					const allNames = targets.map((t) => t.skillArea.name);
					const skillAreaNames = allNames.filter((name, idx) => allNames.indexOf(name) === idx);
					const matchedIds = skillAreaNames
						.map((name) => findOptionId(skillDomainAddressedList, name))
						.filter(Boolean) as string[];

					if (matchedIds.length) {
						setData({ payload: matchedIds, type: ACTIONS.setSkillDomainAddressed });
					}
				}

				if (needsReinforcers) {
					const clientReinforcementTitles = (reinforcementsResponse as Array<{ title: string }>).map(
						(r) => r.title,
					);
					const matchedIds = clientReinforcementTitles
						.map((title) => findOptionId(reinforcementList, title))
						.filter(Boolean) as string[];

					if (matchedIds.length) {
						setData({ payload: matchedIds, type: ACTIONS.setReinforcementResponses });
					}
				}

				setPendingAiSave(true);
			} catch {
				// Session data not available — BT will fill manually
			} finally {
				if (active) setAutoFillCompleteReportId(reportId);
			}
		};

		fetchAndFill();

		return () => {
			active = false;
		};
	}, [
		params,
		soapNote,
		canEdit,
		hydratedReportId,
		reportId,
		data.workedGoals,
		data.frequencyAndDurationOfMaladaptiveBehaviors,
		data.skillDomainAddressedResponses,
		data.reinforcementResponses,
		skillDomainAddressedList,
		reinforcementList,
		optionsReady,
		setData,
		getBehaviorHardDataText,
	]);

	const supervisedAutoFillAttempted = useRef(false);
	const locationAutoFillAttempted = useRef(false);

	useEffect(() => {
		setAutoFillCompleteReportId(null);
		autoFillAttempted.current = false;
		supervisedAutoFillAttempted.current = false;
		locationAutoFillAttempted.current = false;
		behaviorHardDataPromiseRef.current = null;
		workedGoalsHardDataPromiseRef.current = null;
	}, [reportId]);

	useEffect(() => {
		if (!locationList || hydratedReportId !== reportId || !optionsReady) return;

		if (!supervisorSessionInfo.length) {
			setLocations(locationList);

			return;
		}

		// Only filter locations if DSU note is submitted /pendingBT
		const submittedSupervisorSessions = supervisorSessionInfo.filter(
			(x) =>
				x.reportStatus?.status === SoapNotesStatuses.submitted ||
				x.reportStatus?.status === SoapNotesStatuses.pendingBT,
		);

		if (!submittedSupervisorSessions.length) {
			setLocations(locationList);

			return;
		}

		if (canEdit && !supervisedAutoFillAttempted.current && !data.isSessionSupervised) {
			supervisedAutoFillAttempted.current = true;
			setData({ type: ACTIONS.setIsSessionSupervised, payload: true });
		}

		const supervisorLocations = submittedSupervisorSessions.map((x) => x.location);
		const filteredLocationList = locationList.filter((x) =>
			supervisorLocations.find((y) => y.locationType === x.locationType),
		);
		setLocations(filteredLocationList);

		if (canEdit && !locationAutoFillAttempted.current && !data.locationId && filteredLocationList.length === 1) {
			locationAutoFillAttempted.current = true;
			setData({ type: ACTIONS.setLocationId, payload: filteredLocationList[0].id });
		}
	}, [locationList, supervisorSessionInfo, data.isSessionSupervised, data.locationId, hydratedReportId, reportId, optionsReady, canEdit, setData]);

	useEffect(() => {
		if (hydratedReportId !== reportId || !canEdit || data.participantResponses.length) return;
		const payload = participantsList.filter((x) => x.isDefault).map((y) => y.id);
		setData({ type: ACTIONS.setParticipants, payload });
	}, [participantsList, canEdit, hydratedReportId, reportId, data.participantResponses.length, setData]);

	useEffect(() => {
		const sessionId = params.get('sessionId');
		if (!sessionId) return;
		const request = dispatch(getSupervisorSessionsInfo(sessionId));

		return () => request.abort();
	}, [dispatch, params]);

	const levelOfbehaviorHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLevelOfMaladaptiveBehavior });
	};

	const responseToTreatmentHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setClientResponseToTreatment });
	};

	const describeSessionHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setSessionDescription });
	};

	const barrierToTreatmentOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setBarrierToTreatmentOther });
	};

	const clientMoodOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setClientMoodOther });
	};

	const futurePlanOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setFuturePlanOther });
	};

	const reinforcersOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setReinforcementOther });
	};

	const howBarrierAffectHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setHowBarrierAffect });
	};

	const workedGoalsHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setWorkedGoals });
	};

	const describeFrequencyAndDurationHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({
			payload: value,
			type: ACTIONS.setFrequencyAndDurationOfMaladaptiveBehaviors,
		});
	};

	const barriersTreatmentHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setBarrierToTreatmentResponses, payload: value });
	};

	const behaviorTechniquesHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({
			payload: value,
			type: ACTIONS.setBehaviorTreatmentTechniquesResponses,
		});
	};

	const reinforcerHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setReinforcementResponses, payload: value });
	};

	const malaptiveTechniquesHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({
			payload: value,
			type: ACTIONS.setMaladaptiveTechniquesResponses,
		});
	};

	const skillDomainHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({
			payload: value,
			type: ACTIONS.setSkillDomainAddressed,
		});
	};

	const clientMoodHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setClientMoodResponses });
	};

	const futurePlansHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setFuturePlanResponses });
	};

	const locationHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLocationId });
	};

	const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value.toUpperCase(), type: ACTIONS.setSignature });
	};

	const participantsHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setParticipants, payload: value });
	};

	const participantOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setBehaviorTreatmentParticipantOther });
	};

	const isSupervisedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setData({
			type: ACTIONS.setIsSessionSupervised,
			payload: checked,
		});
	};

	const isOtherSelectedForBarriersToTreatment = data.barrierToTreatmentResponses.find(
		(x) => barriersList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForFuturePlans = data.behaviorTreatmentFuturePlanResponses.find(
		(x) => behaviorTreatmentFutureList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForClientMood = data.clientMoodResponses.find(
		(x) => clientMoodList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForReinforcers = data.reinforcementResponses.find(
		(x) => reinforcementList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForParticipants = data.participantResponses.find(
		(x) => participantsList.find((y) => y.id === x)?.text === 'Other',
	);

	const clientId = params.get('clientId') || '';
	const sessionId = params.get('sessionId') || '';

	const optionsLoading = useSelector((state) => state.soapNote.initialLoading);

	const getCurrentFieldValues = useCallback((): Record<string, string> => {
		const idsToText = (ids: string[], list: Array<{ id: string | number; text?: string }>) =>
			ids.map((id) => list.find((o) => String(o.id) === id)?.text).filter(Boolean).join('; ');

		const values: Record<string, string> = {};

		if (data.sessionDescription) values.sessionDescription = data.sessionDescription;
		if (data.howBarrierAffect) values.howBarrierAffectedSession = data.howBarrierAffect;
		if (data.clientMoodResponses.length) {
			values.clientMood = idsToText(data.clientMoodResponses, clientMoodList);
		}

		if (data.clientResponseToTreatment) {
			values.clientResponseToTreatment = clientResponseToTreatmentList.find(
				(o) => o.id === data.clientResponseToTreatment,
			)?.text || '';
		}

		if (data.behaviorTreatmentFuturePlanResponses.length) {
			values.planForFutureSession = idsToText(data.behaviorTreatmentFuturePlanResponses, behaviorTreatmentFutureList);
		}

		if (data.levelOfMaladaptiveBehavior) {
			values.levelOfMaladaptiveBehavior = levelOfMaladaptiveBehaviorList.find(
				(o) => o.id === data.levelOfMaladaptiveBehavior,
			)?.text || '';
		}

		if (data.barrierToTreatmentResponses.length) {
			values.barriersToTreatment = idsToText(data.barrierToTreatmentResponses, barriersList);
		}

		if (data.behaviorTreatmentTechniquesResponses.length) {
			values.behaviorTreatmentTechniques = idsToText(data.behaviorTreatmentTechniquesResponses, technuquiesList);
		}

		if (data.maladaptiveBehaviorTechniquesResponses.length) {
			values.maladaptiveBehaviorTechniques = idsToText(
				data.maladaptiveBehaviorTechniquesResponses,
				maladaptiveBehaviorTechniquesList,
			);
		}

		if (data.participantResponses.length) {
			values.participants = idsToText(data.participantResponses, participantsList);
		}
		if (data.reinforcementResponses.length) {
			values.reinforcers = idsToText(data.reinforcementResponses, reinforcementList);
		}
		if (data.skillDomainAddressedResponses.length) {
			values.skillDomainAddressed = idsToText(data.skillDomainAddressedResponses, skillDomainAddressedList);
		}
		if (data.workedGoals) values.workedGoals = data.workedGoals;
		if (data.frequencyAndDurationOfMaladaptiveBehaviors) {
			values.frequencyAndDurationOfMaladaptiveBehaviors = data.frequencyAndDurationOfMaladaptiveBehaviors;
		}

		return values;
	}, [
		data,
		clientMoodList,
		clientResponseToTreatmentList,
		behaviorTreatmentFutureList,
		levelOfMaladaptiveBehaviorList,
		barriersList,
		technuquiesList,
		reinforcementList,
		maladaptiveBehaviorTechniquesList,
		skillDomainAddressedList,
		participantsList,
	]);

	// Runs the same required-field checks as Submit, without actually submitting — so a BT can see
	// what's still missing right after AI Assist fills a new note, or when reopening a note that
	// already has content. Applies the same red-highlight mechanism used for server-side submit
	// errors, plus a summary for the chip list at the top of the page.
	const [submitValidationIssues, setSubmitValidationIssues] = useState<SubmitValidationIssue[]>([]);
	const [pendingAiValidation, setPendingAiValidation] = useState(false);

	const runSubmitValidation = useCallback(() => {
		const issues = validateBehaviorTreatmentForSubmit(
			data,
			{ barriersList, behaviorTreatmentFutureList, clientMoodList, reinforcementList, participantsList },
			isDoeFunded,
		);

		setSubmitValidationIssues(issues);

		const failingKeys = new Set(issues.map((issue) => issue.key));

		(Object.keys(ERRORS) as Array<keyof typeof ERRORS>).forEach((key) => {
			setErrors({ payload: failingKeys.has(key), type: ERRORS[key] });
		});

		return issues;
	}, [data, barriersList, behaviorTreatmentFutureList, clientMoodList, reinforcementList, participantsList, isDoeFunded]);

	const submitHandler = useCallback(() => {
		const issues = runSubmitValidation();

		if (issues.length) {
			toast.error('Please correct the highlighted fields before submitting.');
			requestAnimationFrame(() => {
				const firstInvalidField = document.querySelector<HTMLElement>('[aria-invalid="true"]');
				firstInvalidField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
				firstInvalidField?.focus({ preventScroll: true });
			});

			return;
		}

		submit();
	}, [runSubmitValidation, submit]);

	const validateSignatureHandler = () => {
		if (validateName(data.signature)) {
			save();
			runSubmitValidation();

			return;
		}
		setErrors({ payload: true, type: ERRORS.Signature });
	};

	const validatedReportIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (hydratedReportId !== reportId) {
			validatedReportIdRef.current = null;
			setSubmitValidationIssues([]);
			(Object.keys(ERRORS) as Array<keyof typeof ERRORS>).forEach((key) => {
				setErrors({ type: ERRORS[key], payload: false });
			});
		}
	}, [hydratedReportId, reportId]);

	useEffect(() => {
		if (!reportId || hydratedReportId !== reportId || autoFillCompleteReportId !== reportId || !optionsReady) return;
		if (validatedReportIdRef.current === reportId) return;
		if (!canEdit && !isPostSubmitEditMode) return;

		validatedReportIdRef.current = reportId;
		runSubmitValidation();
	}, [reportId, hydratedReportId, autoFillCompleteReportId, optionsReady, canEdit, isPostSubmitEditMode, runSubmitValidation]);

	// Trigger 2: right after AI Assist fills a new note. Deferred to the next render (rather than
	// called directly inside handleAiFieldsExtracted) so it reads the just-applied field values,
	// not a stale closure from before the AI fields were dispatched.
	useEffect(() => {
		if (!pendingAiValidation) return;

		setPendingAiValidation(false);
		runSubmitValidation();
	}, [pendingAiValidation, runSubmitValidation]);

	// Trigger 3: any manual field edit. Single source of truth for both the red field-level frames
	// and the red chip summary — fires on blur (not on every keystroke) so a BT isn't shown an error
	// mid-typing, but sees it resolve/reappear as soon as they move to the next field. Individual
	// field handlers used to clear their own boolean error flag directly on change, which desynced
	// from the chip summary (which only `runSubmitValidation` updates) and never re-ran cross-field
	// checks like word count or required-mention — so a fixed field's chip/frame could outlive the fix.
	const handleFieldBlur = useCallback(() => {
		save();
		runSubmitValidation();
	}, [save, runSubmitValidation]);

	const handleAiFieldsExtracted = useCallback(
		(fields: AiParseSoapNoteResponse) => {
			// Existing report and user-entered values stay immutable. Only empty fields and untouched
			// hard-data defaults explicitly approved for server-side composition can be applied.
			if (fields.sessionDescription && !data.sessionDescription) {
				setData({
					payload: [data.sessionDescription, fields.sessionDescription].filter(Boolean).join(' '),
					type: ACTIONS.setSessionDescription,
				});
			}

			if (fields.howBarrierAffectedSession && !data.howBarrierAffect) {
				setData({
					payload: [data.howBarrierAffect, fields.howBarrierAffectedSession].filter(Boolean).join(' '),
					type: ACTIONS.setHowBarrierAffect,
				});
			}

			if (fields.clientMood?.length && !data.clientMoodResponses.length) {
				const ids = fields.clientMood
					.map((text) => findOptionId(clientMoodList, text))
					.filter(Boolean) as string[];

				if (ids.length) {
					setData({ payload: mergeIds(data.clientMoodResponses, ids), type: ACTIONS.setClientMoodResponses });
				}
			}

			if (fields.clientMoodOther && !data.clientMoodOther && !data.clientMoodResponses.length) {
				setData({ payload: fields.clientMoodOther, type: ACTIONS.setClientMoodOther });
			}

			if (fields.clientResponseToTreatment && !data.clientResponseToTreatment) {
				const match = clientResponseToTreatmentList.find(
					(o) => o.text.trim().toLocaleLowerCase() === fields.clientResponseToTreatment!.trim().toLocaleLowerCase(),
				);

				if (match) setData({ payload: match.id, type: ACTIONS.setClientResponseToTreatment });
			}

			if (fields.planForFutureSession?.length && !data.behaviorTreatmentFuturePlanResponses.length) {
				const ids = fields.planForFutureSession
					.map((text) => findOptionId(behaviorTreatmentFutureList, text))
					.filter(Boolean) as string[];

				if (ids.length) {
					setData({
						payload: mergeIds(data.behaviorTreatmentFuturePlanResponses, ids),
						type: ACTIONS.setFuturePlanResponses,
					});
				}
			}

			if (fields.planForFutureSessionOther && !data.behaviorTreatmentFuturePlanOther && !data.behaviorTreatmentFuturePlanResponses.length) {
				setData({ payload: fields.planForFutureSessionOther, type: ACTIONS.setFuturePlanOther });
			}

			if (fields.levelOfMaladaptiveBehavior && !data.levelOfMaladaptiveBehavior) {
				const match = levelOfMaladaptiveBehaviorList.find(
					(o) => o.text.trim().toLocaleLowerCase() === fields.levelOfMaladaptiveBehavior!.trim().toLocaleLowerCase(),
				);

				if (match) setData({ payload: match.id, type: ACTIONS.setLevelOfMaladaptiveBehavior });
			}

			if (fields.barriersToTreatment?.length && !data.barrierToTreatmentResponses.length) {
				const ids = fields.barriersToTreatment
					.map((text) => findOptionId(barriersList, text))
					.filter(Boolean) as string[];

				if (ids.length) {
					setData({
						payload: mergeIds(data.barrierToTreatmentResponses, ids),
						type: ACTIONS.setBarrierToTreatmentResponses,
					});
				}
			}

			if (fields.barrierToTreatmentOther && !data.barrierToTreatmentOther && !data.barrierToTreatmentResponses.length) {
				setData({ payload: fields.barrierToTreatmentOther, type: ACTIONS.setBarrierToTreatmentOther });
			}

			if (fields.behaviorTreatmentTechniques?.length && !data.behaviorTreatmentTechniquesResponses.length) {
				const ids = fields.behaviorTreatmentTechniques
					.map((text) => findOptionId(technuquiesList, text))
					.filter(Boolean) as string[];

				if (ids.length) {
					setData({
						payload: mergeIds(data.behaviorTreatmentTechniquesResponses, ids),
						type: ACTIONS.setBehaviorTreatmentTechniquesResponses,
					});
				}
			}

			if (fields.maladaptiveBehaviorTechniques?.length && !data.maladaptiveBehaviorTechniquesResponses.length) {
				const ids = fields.maladaptiveBehaviorTechniques
					.map((text) => findOptionId(maladaptiveBehaviorTechniquesList, text))
					.filter(Boolean) as string[];

				if (ids.length) {
					setData({
						payload: mergeIds(data.maladaptiveBehaviorTechniquesResponses, ids),
						type: ACTIONS.setMaladaptiveTechniquesResponses,
					});
				}
			}

			if (fields.participants?.length && !data.participantResponses.length) {
				const ids = fields.participants
					.map((text) => findOptionId(participantsList, text))
					.filter(Boolean) as string[];

				if (ids.length) {
					setData({ payload: mergeIds(data.participantResponses, ids), type: ACTIONS.setParticipants });
				}
			}

			if (fields.reinforcers?.length && (!data.reinforcementResponses.length || replaceableHardDataFields.includes('reinforcers'))) {
				const ids = fields.reinforcers
					.map((text) => findOptionId(reinforcementList, text))
					.filter(Boolean) as string[];

				if (ids.length) {
					setData({
						payload: replaceableHardDataFields.includes('reinforcers') ? ids : mergeIds(data.reinforcementResponses, ids),
						type: ACTIONS.setReinforcementResponses,
					});
				}
			}

			if (fields.skillDomainAddressed?.length && (!data.skillDomainAddressedResponses.length || replaceableHardDataFields.includes('skillDomainAddressed'))) {
				const ids = fields.skillDomainAddressed
					.map((text) => findOptionId(skillDomainAddressedList, text))
					.filter(Boolean) as string[];

				if (ids.length) {
					setData({
						payload: replaceableHardDataFields.includes('skillDomainAddressed') ? ids : mergeIds(data.skillDomainAddressedResponses, ids),
						type: ACTIONS.setSkillDomainAddressed,
					});
				}
			}

			if (fields.workedGoals && (!data.workedGoals || replaceableHardDataFields.includes('workedGoals'))) {
				setData({
					payload: fields.workedGoals,
					type: ACTIONS.setWorkedGoals,
				});
			}

			if (
				fields.frequencyAndDurationOfMaladaptiveBehaviors &&
				(!data.frequencyAndDurationOfMaladaptiveBehaviors ||
					replaceableHardDataFields.includes('frequencyAndDurationOfMaladaptiveBehaviors'))
			) {
				setData({
					payload: fields.frequencyAndDurationOfMaladaptiveBehaviors,
					type: ACTIONS.setFrequencyAndDurationOfMaladaptiveBehaviors,
				});
			}

			if (fields.reinforcementOther && !data.reinforcementOther && !data.reinforcementResponses.length) {
				setData({ payload: fields.reinforcementOther, type: ACTIONS.setReinforcementOther });
			}

			setAmbiguousFields((current) => {
				const next = new Set(current);
				const responseKeys: Array<keyof AiParseSoapNoteResponse> = [
					'sessionDescription', 'howBarrierAffectedSession',
					'clientMood', 'clientResponseToTreatment',
					'planForFutureSession', 'levelOfMaladaptiveBehavior', 'barriersToTreatment',
					'behaviorTreatmentTechniques', 'maladaptiveBehaviorTechniques',
					'participants', 'reinforcers', 'skillDomainAddressed', 'workedGoals',
					'frequencyAndDurationOfMaladaptiveBehaviors',
				];
				responseKeys.forEach((key) => {
					const value = fields[key];
					const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value);
					if (hasValue) next.delete(key);
				});
				(fields.ambiguousFields || []).forEach((key) => next.add(key));

				return next;
			});

			setData({ payload: true, type: ACTIONS.setIsAiGenerated });
			setPendingAiSave(true);
			setPendingAiValidation(true);
		},
		[
			setData,
			data,
			clientMoodList,
			clientResponseToTreatmentList,
			behaviorTreatmentFutureList,
			levelOfMaladaptiveBehaviorList,
			barriersList,
			technuquiesList,
			reinforcementList,
			maladaptiveBehaviorTechniquesList,
			skillDomainAddressedList,
			participantsList,
			replaceableHardDataFields,
			setPendingAiValidation,
		],
	);

	// Must be a stable reference — CustomAlert renders this as a JSX component type
	// (`<Content onClose={onClose} />`), so a fresh inline function on every parent
	// re-render would remount AIAssistSoapNote/VoiceTextInput and wipe their local
	// recording/follow-up state (e.g. right after tapping Stop).
	const renderAiAssistContent = useCallback(
		({ onClose }: { onClose: () => void }) => (
			<AIAssistSoapNote
				clientId={clientId}
				sessionId={sessionId}
				reportId={reportId}
				replaceableHardDataFields={replaceableHardDataFields}
				onFieldsExtracted={(fields) => {
					handleAiFieldsExtracted(fields);
					onClose();
				}}
				getCurrentFieldValues={getCurrentFieldValues}
			/>
		),
		[
			clientId,
			sessionId,
			reportId,
			replaceableHardDataFields,
			handleAiFieldsExtracted,
			getCurrentFieldValues,
		],
	);

	useEffect(() => {
		if (pendingAiSave) {
			save();
			setPendingAiSave(false);
		}
	}, [pendingAiSave, save]);

	const confirmAmbiguousField = useCallback((key: string) => {
		setAmbiguousFields((current) => {
			if (!current.has(key)) return current;
			const next = new Set(current);
			next.delete(key);

			return next;
		});
	}, []);

	// Deduped by label for chip display — the same field can fail more than one rule
	// (e.g. empty AND too-short), but should only show once in the summary.
	const uniqueValidationIssues = submitValidationIssues.filter(
		(issue, index) => submitValidationIssues.findIndex((other) => other.label === issue.label) === index,
	);

	return (
		<>
			{soapNote && canEdit && !isPostSubmitEditMode && !data.isAiGenerated && (
				<>
					<div
						className="marginBottom16"
						style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
					>
						<CustomButton
							title="Fill with AI"
							buttonType="primary"
							sizeType="sm"
							startIcon={<AutoAwesome fontSize="small" />}
							onClick={() => setAiModalOpen(true)}
							disabled={optionsLoading || hydratedReportId !== reportId || !hardDataDefaultsApplied}
							aria-label="Fill SOAP note with AI"
						/>
					</div>
					<CustomAlert
						title="Fill SOAP Note with AI"
						open={aiModalOpen}
						onClose={() => setAiModalOpen(false)}
						maxWidth="sm"
						mobileBottomSheet
						Content={renderAiAssistContent}
					/>
				</>
			)}
			{soapNote && canEdit && !isPostSubmitEditMode && (
				<>
					{ambiguousFields.size > 0 && (
						<div
							className="marginBottom16"
							style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
						>
							<div
								style={{
									border: `1px solid ${ORANGE.BASE}`,
									backgroundColor: ORANGE.QUATERNARY,
									borderRadius: '999px',
									padding: '4px 12px',
								}}
							>
								<Text
									title={`${ambiguousFields.size} FIELD${ambiguousFields.size === 1 ? '' : 'S'} TO CONFIRM`}
									type="pxs"
									fontWeight="semiBold"
									color={ORANGE.DARK[16]}
								/>
							</div>
						</div>
					)}
					{uniqueValidationIssues.length > 0 && (
						<div
							className="marginBottom16"
							style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}
						>
							{uniqueValidationIssues.map((issue) => (
								<div
									key={issue.label}
									title={issue.message}
									style={{
										border: `1px solid ${RED.BASE}`,
										backgroundColor: RED.TERTIARY,
										borderRadius: '999px',
										padding: '4px 12px',
									}}
								>
									<Text title={issue.label} type="pxs" fontWeight="semiBold" color={RED.DARK[16]} />
								</div>
							))}
						</div>
					)}
				</>
			)}
			<TimeInfo disabled={getFieldDisabled('startTime')} save={save} />
			{user.role.section.id === AdminTypes.bt && (
				<CustomInput label="Behavior Technician:" value={user.fullName} className="marginBottom16" disabled />
			)}
			{Boolean(supervisorSessionInfo.length) && Boolean(supervisor) && (
				<div className="padding8" style={{ minWidth: '300px' }}>
					<Text title="Supervised session info:" type="plg" fontWeight="semiBold" />
					<InfoRow>
						<Text title="Supervisor:" type="plg" fontWeight="semiBold" />
						<Text title={supervisor?.fullName ?? ''} />
					</InfoRow>
					{supervisorSessionInfo.map((x) => (
						<>
							{Boolean(x.location) && (
								<InfoRow>
									<Text title="Location:" type="plg" fontWeight="semiBold" />
									<Text type="plg" fontWeight="medium" title={x.location.name} />
								</InfoRow>
							)}
							<InfoRow>
								<Text title="Date:" type="plg" fontWeight="semiBold" />
								<Text type="plg" fontWeight="medium" title={`${dayjs(x.date).format('MM/DD/YYYY')}`} />
							</InfoRow>
							<InfoRow>
								<Text title="Start time:" type="plg" fontWeight="semiBold" />
								<Text type="plg" fontWeight="medium" title={`${parseWallClockTime(x.startTime).format('hh:mm A')}`} />
							</InfoRow>
							<InfoRow>
								<Text title="End time:" type="plg" fontWeight="semiBold" />
								<Text type="plg" fontWeight="medium" title={`${parseWallClockTime(x.endTime).format('hh:mm A')}`} />
							</InfoRow>
						</>
					))}
				</div>
			)}
			<CustomSelect
				label="Location:"
				data={locations}
				value={data.locationId ? data.locationId : ''}
				error={fieldErrors.LocationId}
				errorMessage={fieldErrors.LocationId ? (data.locationId ? BT_LOCATION_MISMATCH_ERROR : 'Location is required.') : ''}
				setValue={locationHandler}
				className="marginBottom16"
				disabled={getFieldDisabled('locationId')}
				onBlur={handleFieldBlur}
			/>
			<CustomCheckBox
				style={{
					marginBottom: '16px',
				}}
				item={{
					label: 'Session was supervised by the BCBA',
					checked: data.isSessionSupervised,
					id: '0',
					disabled: getFieldDisabled('isSessionSupervised'),
				}}
				onChange={isSupervisedHandler}
				onBlur={handleFieldBlur}
			/>
			<ChipSelect
				label="Session Participants:"
				data={participantsList}
				initialValue={data.participantResponses}
				setValue={participantsHandler}
				error={fieldErrors.ParticipantResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('participantResponses')}
				onBlur={handleFieldBlur}
				warning={ambiguousFields.has('participants')}
				onConfirm={() => confirmAmbiguousField('participants')}
			/>
			{isOtherSelectedForParticipants && (
				<CustomInput
					label="If other, please describe: "
					value={data.behaviorTreatmentParticipantOther}
					onChange={participantOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('behaviorTreatmentParticipantOther')}
					onBlur={handleFieldBlur}
					error={fieldErrors.BehaviorTreatmentParticipantOther}
				/>
			)}
			<CustomInput
				label="Describe the session: "
				value={data.sessionDescription}
				onChange={describeSessionHandler}
				className="marginBottom16"
				multiline
				disabled={getFieldDisabled('sessionDescription')}
				onBlur={handleFieldBlur}
				error={fieldErrors.SessionDescription}
				warning={!fieldErrors.SessionDescription && ambiguousFields.has('sessionDescription')}
				onConfirm={() => confirmAmbiguousField('sessionDescription')}
			/>
			<ChipSelect
				label="Barriers to Treatment:"
				data={barriersList}
				setValue={barriersTreatmentHandler}
				className="marginBottom16"
				initialValue={data.barrierToTreatmentResponses}
				disabled={getFieldDisabled('barrierToTreatmentResponses')}
				onBlur={handleFieldBlur}
				error={fieldErrors.BarrierToTreatmentResponses}
				warning={!fieldErrors.BarrierToTreatmentResponses && ambiguousFields.has('barriersToTreatment')}
				onConfirm={() => confirmAmbiguousField('barriersToTreatment')}
			/>
			{isOtherSelectedForBarriersToTreatment && (
				<CustomInput
					label="If other, please describe: "
					value={data.barrierToTreatmentOther}
					onChange={barrierToTreatmentOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('barrierToTreatmentOther')}
					onBlur={handleFieldBlur}
					error={fieldErrors.BarrierToTreatmentOther}
				/>
			)}
			<CustomInput
				label="How did the above barrier affect the session? "
				value={data.howBarrierAffect}
				onChange={howBarrierAffectHandler}
				className="marginBottom16"
				multiline
				disabled={getFieldDisabled('howBarrierAffect')}
				onBlur={handleFieldBlur}
				error={fieldErrors.HowBarrierAffect}
				warning={!fieldErrors.HowBarrierAffect && ambiguousFields.has('howBarrierAffectedSession')}
				onConfirm={() => confirmAmbiguousField('howBarrierAffectedSession')}
			/>
			<ChipSelect
				label="Skill domains addressed: "
				data={skillDomainAddressedList}
				setValue={skillDomainHandler}
				initialValue={data.skillDomainAddressedResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('skillDomainAddressedResponses')}
				onBlur={handleFieldBlur}
				error={fieldErrors.SkillDomainAddressedResponses}
				warning={!fieldErrors.SkillDomainAddressedResponses && ambiguousFields.has('skillDomainAddressed')}
				onConfirm={() => confirmAmbiguousField('skillDomainAddressed')}
			/>
			<ChipSelect
				label="Techniques Used: "
				data={technuquiesList}
				setValue={behaviorTechniquesHandler}
				initialValue={data.behaviorTreatmentTechniquesResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('behaviorTreatmentTechniquesResponses')}
				onBlur={handleFieldBlur}
				error={fieldErrors.BehaviorTreatmentTechniquesResponses}
				warning={!fieldErrors.BehaviorTreatmentTechniquesResponses && ambiguousFields.has('behaviorTreatmentTechniques')}
				onConfirm={() => confirmAmbiguousField('behaviorTreatmentTechniques')}
			/>
			<ChipSelect
				label="Reinforcers Used in Sessions: "
				data={reinforcementList}
				setValue={reinforcerHandler}
				initialValue={data.reinforcementResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('reinforcementResponses')}
				onBlur={handleFieldBlur}
				error={fieldErrors.Reinforcement}
				warning={!fieldErrors.Reinforcement && ambiguousFields.has('reinforcers')}
				onConfirm={() => confirmAmbiguousField('reinforcers')}
			/>
			{isOtherSelectedForReinforcers && (
				<CustomInput
					label="If other, please describe: "
					value={data.reinforcementOther}
					onChange={reinforcersOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('reinforcementOther')}
					onBlur={handleFieldBlur}
					error={fieldErrors.ReinforcementOther}
				/>
			)}
			<CustomInput
				label={`Describe ${isDoeFunded ? 2 : 3} goals that were addressed in the session:`}
				value={data.workedGoals}
				onChange={workedGoalsHandler}
				className="marginBottom16"
				multiline
				disabled={getFieldDisabled('workedGoals')}
				onBlur={handleFieldBlur}
				error={fieldErrors.WorkedGoals}
				warning={!fieldErrors.WorkedGoals && ambiguousFields.has('workedGoals')}
				onConfirm={() => confirmAmbiguousField('workedGoals')}
			/>
			<CustomSelect
				label="Level of maladaptive behavior:"
				data={levelOfMaladaptiveBehaviorList}
				value={`${data.levelOfMaladaptiveBehavior}`}
				setValue={levelOfbehaviorHandler}
				className="marginBottom16"
				disabled={getFieldDisabled('levelOfMaladaptiveBehavior')}
				onBlur={handleFieldBlur}
				error={fieldErrors.LevelOfMaladaptiveBehavior}
				warning={!fieldErrors.LevelOfMaladaptiveBehavior && ambiguousFields.has('levelOfMaladaptiveBehavior')}
				onConfirm={() => confirmAmbiguousField('levelOfMaladaptiveBehavior')}
			/>
			<ChipSelect
				label="Maladaptive behavior techniques used:"
				data={maladaptiveBehaviorTechniquesList}
				setValue={malaptiveTechniquesHandler}
				className="marginBottom16"
				initialValue={data.maladaptiveBehaviorTechniquesResponses}
				disabled={getFieldDisabled('maladaptiveBehaviorTechniquesResponses')}
				onBlur={handleFieldBlur}
				error={fieldErrors.MaladaptiveBehaviorTechniquesResponses}
				warning={!fieldErrors.MaladaptiveBehaviorTechniquesResponses && ambiguousFields.has('maladaptiveBehaviorTechniques')}
				onConfirm={() => confirmAmbiguousField('maladaptiveBehaviorTechniques')}
			/>
			<CustomInput
				label="Describe the frequency and duration of the maladaptive behavior and the techniques used as an intervention:"
				value={data.frequencyAndDurationOfMaladaptiveBehaviors}
				onChange={describeFrequencyAndDurationHandler}
				className="marginBottom16"
				multiline
				description={frequencyAndDurationDescription}
				disabled={getFieldDisabled('frequencyAndDurationOfMaladaptiveBehaviors')}
				onBlur={handleFieldBlur}
				error={fieldErrors.FrequencyAndDurationOfMaladaptiveBehaviors}
				warning={!fieldErrors.FrequencyAndDurationOfMaladaptiveBehaviors && ambiguousFields.has('frequencyAndDurationOfMaladaptiveBehaviors')}
				onConfirm={() => confirmAmbiguousField('frequencyAndDurationOfMaladaptiveBehaviors')}
			/>

			<ChipSelect
				label="Client mood:"
				data={clientMoodList}
				setValue={clientMoodHandler}
				className="marginBottom16"
				initialValue={data.clientMoodResponses}
				disabled={getFieldDisabled('clientMoodResponses')}
				onBlur={handleFieldBlur}
				error={fieldErrors.ClientMoodResponses}
				warning={!fieldErrors.ClientMoodResponses && ambiguousFields.has('clientMood')}
				onConfirm={() => confirmAmbiguousField('clientMood')}
			/>
			{isOtherSelectedForClientMood && (
				<CustomInput
					label="If other, please describe: "
					value={data.clientMoodOther}
					onChange={clientMoodOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('clientMoodOther')}
					onBlur={handleFieldBlur}
					error={fieldErrors.ClientMoodOther}
				/>
			)}
			<CustomSelect
				label="Client response to treatment:"
				data={clientResponseToTreatmentList}
				value={`${data.clientResponseToTreatment}`}
				setValue={responseToTreatmentHandler}
				className="marginBottom16"
				disabled={getFieldDisabled('clientResponseToTreatment')}
				onBlur={handleFieldBlur}
				error={fieldErrors.ClientResponseToTreatment}
				warning={!fieldErrors.ClientResponseToTreatment && ambiguousFields.has('clientResponseToTreatment')}
				onConfirm={() => confirmAmbiguousField('clientResponseToTreatment')}
			/>
			<ChipSelect
				label="Plan for Future Session:"
				data={behaviorTreatmentFutureList}
				setValue={futurePlansHandler}
				initialValue={data.behaviorTreatmentFuturePlanResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('behaviorTreatmentFuturePlanResponses')}
				onBlur={handleFieldBlur}
				error={fieldErrors.BehaviorTreatmentFuturePlanResponses}
				warning={!fieldErrors.BehaviorTreatmentFuturePlanResponses && ambiguousFields.has('planForFutureSession')}
				onConfirm={() => confirmAmbiguousField('planForFutureSession')}
			/>
			{isOtherSelectedForFuturePlans && (
				<CustomInput
					label="If other, please describe: "
					value={data.behaviorTreatmentFuturePlanOther}
					onChange={futurePlanOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('behaviorTreatmentFuturePlanOther')}
					onBlur={handleFieldBlur}
					error={fieldErrors.FuturePlanOther}
				/>
			)}
			<Text title="Session Details" type="h4" font="header" fontWeight="semiBold" className="marginBottom16 title" />
			<Targets />
			<Behaviors />
			<SignatureInput
				error={fieldErrors.Signature}
				value={data.signature}
				setValue={signatureHandler}
				disabled={getFieldDisabled('signature')}
				onBlur={validateSignatureHandler}
			/>
			<div>
				<Text title="Date/time of signature:" type="psm" fontWeight="semiBold" />
				<Text title={signatureDate} type="psm" />
			</div>
			{soapNote?.bcbaSignature && (
				<>
					<div style={{ marginTop: '16px' }} />
					<SignatureInput
						label="BCBA Signature:"
						value={soapNote.bcbaSignature}
						setValue={() => null}
						disabled
						onBlur={() => null}
						error={false}
					/>
					{soapNote?.bcbaSignedAt && (
						<div>
							<Text title="Date/time of BCBA signature:" type="psm" fontWeight="semiBold" />
							<Text title={dayjs.utc(soapNote.bcbaSignedAt).local().format('MM/DD/YYYY hh:mm A')} type="psm" />
						</div>
					)}
				</>
			)}
			<SoapNoteButtons save={save} submit={submitHandler} closeConfirmationOnSubmit />
		</>
	);
};

export default BehaviorTreatmentData;
